import { Component, OnInit, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective } from '@ngx-translate/core';

import { DetailedQuote } from '../../../../domain/quote.domain';
import { TimeRangeSelector, FrequencySelector, ChartTypeSelector } from '../quote.constants';
import { DisplayTimeSeries, QuoteChartSize, QuoteTab, QuoteTimeSeriesType } from '../domain';
import { TabsComponent } from '../../../../components/tabs/tabs.component';
import { TabComponent } from '../../../../components/tabs/tab.component';
import { DropdownComponent } from '../../../../components/dropdown/dropdown.component';
import { DropdownItemComponent } from '../../../../components/dropdown/dropdown-item.component';
import { NotificationComponent } from '../../../../components/notification/notification.component';
import { SplitButtonsComponent } from '../../../../components/split-buttons/split-buttons.component';
import { SplitButtonTitleComponent } from '../../../../components/split-buttons/split-button-title.component';
import { SplitButtonComponent } from '../../../../components/split-buttons/split-button.component';
import { IconComponent } from '../../../../components/icon/icon.component';
import { IdService } from '../../../../services/id.service';
import { ChartComponent } from '../../../../components/charts/chart.component';
import type Highcharts from 'highcharts/highstock';

@Component({
  selector: 'td-wb-quote-chart',
  templateUrl: 'quote-chart.component.html',
  styleUrls: ['quote-chart.component.scss'],
  imports: [
    TranslateDirective,
    TabsComponent,
    TabComponent,
    DropdownComponent,
    FormsModule,
    DropdownItemComponent,
    NotificationComponent,
    SplitButtonsComponent,
    SplitButtonTitleComponent,
    SplitButtonComponent,
    IconComponent,
    ChartComponent
  ]
})
export class QuoteChartComponent implements OnInit {
  private readonly idService = inject(IdService);

  private static readonly ID_PREFIX: string = 'td-wb-quote-chart__chart-container';

  readonly quote = input<DetailedQuote>(undefined);
  readonly noChartData = input<boolean>(undefined);
  readonly closePrice = input<string>(undefined);
  readonly displayTimeSeries = input<DisplayTimeSeries>(undefined);
  readonly options = input<Highcharts.Options>(undefined);
  readonly closeSeries = input<QuoteTimeSeriesType>(undefined);
  readonly chartSize = input<QuoteChartSize>(undefined);
  readonly onTabChange = output<number>();
  readonly onTimeRangeChange = output<TimeRangeSelector>();
  readonly onFrequencyChange = output<FrequencySelector>();
  readonly onTypeChange = output<ChartTypeSelector>();

  activeButtonId: number = 0;
  renderTo: string;
  activeTab: QuoteTab;

  /**
   * Computed options with renderTo injected, creating a new reference
   * whenever options() changes so OnPush children detect the update.
   */
  readonly chartOptions = computed<Highcharts.Options | undefined>(() => {
    const opts = this.options();
    if (opts === undefined) {
      return undefined;
    }
    const chart = opts.chart !== undefined
      ? { ...opts.chart, renderTo: this.renderTo }
      : { renderTo: this.renderTo };
    return { ...opts, chart };
  });

  constructor() {
    this.renderTo = this.idService.generateNamespaceSequentialId(QuoteChartComponent.ID_PREFIX);
  }

  ngOnInit(): void {
    const timeRanges: Array<TimeRangeSelector> = this.displayTimeSeries().timeRanges;
    if (timeRanges !== undefined) {
      const index = timeRanges.findIndex((timeRange: TimeRangeSelector): boolean => timeRange === this.displayTimeSeries().selectTimeRange);
      this.activeButtonId = index + 1;
    }
    const displayTimeSeries = this.displayTimeSeries();
    if (displayTimeSeries.tabs !== undefined) {
      this.activeTab = displayTimeSeries.tabs[displayTimeSeries.value];
    }
  }

  /**
   * Return true if quote is halted
   *
   * @returns boolean
   */
  isHalted(): boolean {
    const quote = this.quote();
    return quote === undefined || quote.securityHalted;
  }

  /**
   * Return true if quote is not halted and chart is not customizable
   *
   * @returns boolean
   */
  showTabs(): boolean {
    const quote = this.quote();
    return quote !== undefined && !quote.securityHalted && !this.displayTimeSeries().customizedChartEnabled;
  }

  /**
   * Return true if quote is not halted and chart is customizable
   *
   * @returns
   */
  showDropdowns(): boolean {
    const quote = this.quote();
    return quote !== undefined && !quote.securityHalted && this.displayTimeSeries().customizedChartEnabled;
  }

  /**
   * Return true if chart has data and quote is not halted
   *
   * @returns
   */
  showChart(): boolean {
    return !this.noChartData() && !this.quote().securityHalted && this.chartOptions() !== undefined;
  }

  /**
   * Trigger tab change event by dropdown
   *
   * @param tab
   */
  onTabDropdownChangeHandler(tab: QuoteTab): void {
    const index = this.displayTimeSeries().tabs.findIndex((x: QuoteTab): boolean => x === tab);
    this.onTabChangeHandler(index);
  }

  /**
   * Trigger tab change event
   *
   * @param tabId
   */
  onTabChangeHandler(tabId: number): void {
    const displayTimeSeries = this.displayTimeSeries();
    if (displayTimeSeries !== undefined) {
      displayTimeSeries.value = tabId;
      this.activeTab = displayTimeSeries.tabs[displayTimeSeries.value];
    }
    this.onTabChange.emit(tabId);
  }

  /**
   * Trigger tab change event
   *
   * @param timeRange
   */
  onTimeRangeChangeHandler(timeRange: TimeRangeSelector): void {
    this.displayTimeSeries().selectTimeRange = timeRange;
    this.onTimeRangeChange.emit(timeRange);
  }

  /**
   * Trigger frequency change event
   *
   * @param frequency
   */
  onFrequencyChangeHandler(frequency: FrequencySelector): void {
    this.displayTimeSeries().selectFrequency = frequency;
    this.onFrequencyChange.emit(frequency);
  }

  /**
   * Trigger type change event
   *
   * @param type
   */
  onTypeChangeHandler(type: ChartTypeSelector): void {
    this.displayTimeSeries().selectType = type;
    this.onTypeChange.emit(type);
  }

  /**
   * TrackBy for dropdown
   *
   * @param range
   * @returns type
   */
  trackByTimeRangeType(_: number, range: TimeRangeSelector): string {
    return range.type;
  }

  trackByChartType(_: number, range: ChartTypeSelector): string {
    return range.type;
  }

  /**
   * Get active time range button
   *
   * @returns activeButtonId
   */
  onActiveButtonIdChangeHandler(buttonId: number): void {
    this.activeButtonId = buttonId;
    if (this.activeButtonId <= this.displayTimeSeries().timeRanges.length) {
      this.onTimeRangeChangeHandler(this.displayTimeSeries().timeRanges[this.activeButtonId - 1]);
    }
  }

  castToNumber(value: string | number): number {
    return value as number;
  }

  castToString(value: number): string {
    return String(value);
  }
}
